import { Search } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import type { ResumeContextValue } from "@/app/types/resume";

import tips from "../../../../assets/copy/tips";
import ErrorToast from "../../../../components/ErrorToast";
import EditorListItems from "../../../../components/ResumeBuilder/EditorListItems";
import type { EditorListItem } from "../../../../components/ResumeBuilder/EditorListItems/EditorListItems";
import SectionActions from "../../../../components/ResumeBuilder/SectionActions";
import Spinner from "../../../../components/Spinner";
import TextEditor from "../../../../components/TextEditor";
import Tips from "../../../../components/Tips";
import ResumeContext from "../../../../context/ResumeContext";
import { convertArrayToUl } from "../../../../handlers/utilityFunctions";
import { Affiliation } from "../../../../models/resume/affiliation";
import { StaticValue } from "../../../../seed/constants";
import { usePatchResumeSection } from "../../../../services/create-resume/queries";
import { RESUME_SECTIONS } from "../../../../services/create-resume/resumeSections";
import affiliationListSuggestions from "./affiliationList";

interface PatchLedger {
	affiliations: { data: Parameters<typeof convertArrayToUl>[0] };
}

interface PatchError {
	response: { data: { message: string | string[] } };
}

const Affiliations = () => {
	const [cookies] = useCookies(["resume"]);
	const navigate = useNavigate();

	const staticValue = new StaticValue();
	const patchSection = usePatchResumeSection();
	const staticValues = staticValue.getDefaultValues();
	const [serverErrors, setServerErrors] = useState<string[]>([]);
	const [affiliationList, setAffiliationList] = useState<EditorListItem[]>([...affiliationListSuggestions]);

	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;

	//Declaring the affiliations state
	const [affiliations, setAffiliations] = useState(resumeData.extras.affiliations);

	const [userInput, setUserInput] = useState(resumeData.contactInfo.resumeTitle);

	useEffect(() => {
		setAffiliations(resumeData.extras.affiliations);
	}, [resumeData]);

	const addAffiliations = () => {
		const payload = new Affiliation(affiliations);
		const request = payload.getAffiliationRequest();

		const resumeId = cookies[staticValues.localStorageResumeId];

		patchSection.mutate({ resumeId, section: RESUME_SECTIONS.AFFILIATION, data: request } as never, {
			onSuccess: successPatchAffiliation,
			onError: errorPatchAffiliation,
		});
	};

	function successPatchAffiliation(ledger: PatchLedger) {
		const resArray = ledger.affiliations.data;

		const affiliationList = convertArrayToUl(resArray);

		setResumeData((prevState) => {
			return {
				...prevState,
				extras: {
					...prevState.extras,
					affiliations: affiliationList,
				},
			};
		});

		navigate("/resume/extras");
	}

	function errorPatchAffiliation(data: Error) {
		const serverResponse = (data as unknown as PatchError).response.data;
		Array.isArray(serverResponse.message)
			? setServerErrors((prev) => {
					return [...prev, ...serverResponse.message];
				})
			: setServerErrors((prev) => {
					return [...prev, serverResponse.message as string];
				});
	}

	useEffect(() => {
		setUserInput(resumeData.contactInfo.resumeTitle);
	}, [resumeData]);

	return (
		<div className="min-h-screen">
			{serverErrors.map((item, index) => {
				return <ErrorToast error={item} key={index} />;
			})}
			<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
				<div>
					<div className="mb-3 flex items-start justify-between gap-3">
						<p className="text-gray-700">Add details about your professional affiliations.</p>
						<Tips content={tips.affiliations} />
					</div>

					<div className="mb-4">
						<div className="flex">
							<Input type="text" className="rounded-r-none" placeholder={userInput} />
							<Button type="button" className="rounded-l-none md:px-10" aria-label="Search">
								<Search className="size-4" />
							</Button>
						</div>
					</div>
					<EditorListItems
						editorListItems={affiliationList}
						editorContent={affiliations}
						setEditorContent={setAffiliations}
						setEditorListItems={setAffiliationList}
					/>
				</div>
				<div className="mt-4 flex flex-col justify-end">
					<TextEditor value={affiliations} setValue={setAffiliations} placeholder="Add your affiliations here..." />
				</div>
				<SectionActions className="flex items-center justify-end gap-2">
					<Button
						type="button"
						variant="secondary"
						onClick={() => {
							navigate("/resume/extras");
						}}
					>
						Cancel
					</Button>
					<Button type="button" onClick={addAffiliations}>
						{patchSection.isPending ? (
							<>
								Loading <Spinner />
							</>
						) : (
							"Continue"
						)}
					</Button>
				</SectionActions>
			</div>
		</div>
	);
};

export default Affiliations;
